import React, { useEffect, useMemo, useRef, useState } from "react";
import { refreshOrderPaymentStatus as refreshOrderPaymentStatusClient } from "../../utils/firebaseFunctions";
import { doc, getDoc } from "firebase/firestore";
import { db } from "../../firebase";

function toDateSafe(ts) {
  if (!ts) return null;
  if (ts instanceof Date) return ts;
  if (typeof ts?.toDate === "function") {
    try {
      return ts.toDate();
    } catch (_) {
      return null;
    }
  }
  if (typeof ts === "object" && ts !== null && typeof ts.seconds === "number") {
    return new Date(ts.seconds * 1000);
  }
  if (typeof ts === "number") {
    const ms = ts < 1e12 ? ts * 1000 : ts;
    return new Date(ms);
  }
  if (typeof ts === "string") {
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

function formatDateTime(val) {
  const d = toDateSafe(val);
  if (!d) return "Unknown date";
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

function formatShortDate(val) {
  const d = toDateSafe(val);
  if (!d) return "Unknown date";
  return d.toLocaleDateString(undefined, {
    month: "2-digit",
    day: "2-digit",
    year: "2-digit",
  });
}

function formatMoney(val) {
  const n = typeof val === "string" ? Number(val) : Number(val ?? 0);
  const safe = Number.isFinite(n) ? n : 0;
  return safe.toLocaleString(undefined, {
    style: "currency",
    currency: "USD",
  });
}

function getInvoiceTotal(order) {
  if (!order) return null;
  const invoice = order.invoice || {};

  const candidates = [
    invoice.balance,
    invoice.total,
    invoice.amount,
    invoice.amountDue,
    invoice.amount_due,
    invoice.subtotal,
  ];

  for (const val of candidates) {
    if (val == null) continue;
    const n =
      typeof val === "string"
        ? Number(val)
        : typeof val === "object" && val.amount != null
          ? Number(val.amount)
          : Number(val);
    if (Number.isFinite(n)) return n;
  }

  const fallback =
    typeof order.total === "string"
      ? Number(order.total)
      : Number(order.total ?? 0);
  return Number.isFinite(fallback) ? fallback : null;
}

function getPaymentStatusLabel(order) {
  const raw = String(order?.paymentStatus || "").trim();
  return raw || "Unknown";
}

function normalizeInvoiceStatusLabel(raw) {
  const key = String(raw || "")
    .trim()
    .toLowerCase()
    .replace(/[_\s-]+/g, "");
  if (!key) return "";
  if (key === "paid") return "Paid";
  if (key === "sent") return "Sent";
  if (key === "overdue") return "Overdue";
  if (key === "partial" || key === "partiallypaid") return "Partial";
  return "";
}

function getOrderInvoiceStatusLabel(order) {
  const fromPaymentStatus = normalizeInvoiceStatusLabel(order?.paymentStatus);
  if (fromPaymentStatus) return fromPaymentStatus;
  const fromInvoiceStatus = normalizeInvoiceStatusLabel(
    order?.invoice?.status ||
      order?.invoice?.invoice_status ||
      order?.invoiceStatusRaw
  );
  if (fromInvoiceStatus) return fromInvoiceStatus;
  return "";
}

function summarizeInvoiceStatusLabel(orders) {
  const labels = (Array.isArray(orders) ? orders : [])
    .map((o) => getOrderInvoiceStatusLabel(o))
    .filter(Boolean);
  if (!labels.length) return "Sent";
  const keys = labels.map((l) => l.toLowerCase());

  if (keys.includes("overdue")) return "Overdue";
  if (keys.includes("partial")) return "Partial";

  const unique = new Set(keys);
  if (unique.size > 1) return "Partial";
  return labels[0];
}

function getStatusPillStyle(label) {
  const normalized = String(label || "").toLowerCase();
  if (normalized === "paid") {
    return {
      border: "1px solid #bbf7d0",
      background: "#f0fdf4",
      color: "#166534",
    };
  }
  if (normalized === "overdue") {
    return {
      border: "1px solid #fecaca",
      background: "#fef2f2",
      color: "#b91c1c",
    };
  }
  if (normalized === "partial") {
    return {
      border: "1px solid #fde68a",
      background: "#fffbeb",
      color: "#92400e",
    };
  }
  if (normalized === "sent") {
    return {
      border: "1px solid #bfdbfe",
      background: "#eff6ff",
      color: "#1d4ed8",
    };
  }
  // Pending / default
  return {
    border: "1px solid var(--ao-border)",
    background: "#f8fafc",
    color: "#0f172a",
  };
}

function summarizePaymentStatuses(orders) {
  const labels = (Array.isArray(orders) ? orders : [])
    .map((o) => getPaymentStatusLabel(o))
    .filter((label) => label && label !== "Unknown");
  if (!labels.length) return "Unknown";
  const uniqueKeys = Array.from(
    new Set(labels.map((l) => l.toLowerCase().replace(/\s+/g, "")))
  );
  if (uniqueKeys.length === 1) return labels[0];
  return "Mixed";
}

function isOverdue(order) {
  const statusRaw = String(order?.paymentStatus || "")
    .trim()
    .toLowerCase();
  const invoiceStatusRaw = String(
    order?.invoice?.status ||
      order?.invoice?.invoice_status ||
      order?.invoiceStatusRaw ||
      ""
  )
    .trim()
    .toLowerCase();
  return statusRaw === "overdue" || invoiceStatusRaw === "overdue";
}

function downloadInvoice(pdfUrl, filename) {
  if (!pdfUrl || typeof window === "undefined") return;
  try {
    const link = document.createElement("a");
    link.href = pdfUrl;
    if (filename) link.download = filename;
    link.target = "_blank";
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    console.error("Failed to trigger invoice download:", err);
  }
}

export default function InvoiceHistory({
  userOrders,
  stackedOrderIds,
  freshbooksClientId,
}) {
  const [orders, setOrders] = useState(
    Array.isArray(userOrders) ? userOrders : []
  );
  const stackedIds = Array.isArray(stackedOrderIds) ? stackedOrderIds : [];
  const hasStackedInfo = stackedIds.length > 0;
  const [activeTab, setActiveTab] = useState("unconsolidated");
  const lastRefreshRef = useRef(0);
  const [fbBalance, setFbBalance] = useState(null);
  const [fbOverdue, setFbOverdue] = useState(null);
  const [fbBalanceLoading, setFbBalanceLoading] = useState(false);
  const [fbBalanceError, setFbBalanceError] = useState("");
  const [isRefreshing, setIsRefreshing] = useState(false);

  const freshbooksClientIdValue = (() => {
    if (freshbooksClientId == null) return null;
    if (typeof freshbooksClientId === "string") {
      const trimmed = freshbooksClientId.trim();
      return trimmed ? trimmed : null;
    }
    if (typeof freshbooksClientId === "number") {
      return Number.isFinite(freshbooksClientId)
        ? String(freshbooksClientId)
        : null;
    }
    if (typeof freshbooksClientId === "object") {
      const candidate =
        freshbooksClientId.id ??
        freshbooksClientId.clientId ??
        freshbooksClientId.client_id ??
        null;
      if (typeof candidate === "string") {
        const trimmed = candidate.trim();
        return trimmed ? trimmed : null;
      }
      if (typeof candidate === "number") {
        return Number.isFinite(candidate) ? String(candidate) : null;
      }
    }
    return null;
  })();

  // Keep local orders in sync when parent prop changes
  useEffect(() => {
    setOrders(Array.isArray(userOrders) ? userOrders : []);
  }, [userOrders]);

  // Ensure any orders referenced in stackedOrders.{userId}.orders
  // are present in our local orders list so their invoices can appear
  // in the unconsolidated list.
  useEffect(() => {
    const ids = Array.isArray(stackedIds) ? stackedIds : [];
    if (!ids.length) return;

    let cancelled = false;
    (async () => {
      try {
        const existingIds = new Set(
          (Array.isArray(orders) ? orders : []).map((o) =>
            String(o?.id || "").trim()
          )
        );

        const toFetch = ids
          .map((id) => String(id || "").trim())
          .filter((id) => id && !existingIds.has(id));

        if (!toFetch.length) return;

        console.log(
          "[InvoiceHistory] Fetching stacked order docs for missing IDs",
          toFetch
        );

        const fetched = [];
        for (const orderId of toFetch) {
          try {
            const snap = await getDoc(doc(db, "orders", orderId));
            if (!snap.exists()) {
              console.warn(
                "[InvoiceHistory] stackedOrders references missing order",
                orderId
              );
              continue;
            }
            fetched.push({ id: snap.id, ...snap.data() });
          } catch (err) {
            console.error(
              "[InvoiceHistory] Failed to fetch stacked order",
              orderId,
              err
            );
          }
          if (cancelled) return;
        }

        if (!fetched.length || cancelled) return;

        setOrders((prev) => {
          const base = Array.isArray(prev) ? prev : [];
          const seen = new Set(base.map((o) => String(o?.id || "").trim()));
          const dedupedNew = fetched.filter((o) => {
            const id = String(o?.id || "").trim();
            return id && !seen.has(id);
          });
          return dedupedNew.length ? [...base, ...dedupedNew] : base;
        });
      } catch (err) {
        console.error(
          "[InvoiceHistory] Unexpected error while hydrating stacked orders",
          err
        );
      }
    })();

    return () => {
      cancelled = true;
    };
    // We intentionally only depend on stackedIds here; orders are merged via setOrders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stackedIds]);

  // Debug: for each stackedOrders.{userId}.orders entry, log the
  // corresponding order + invoice info that will feed the unconsolidated list.
  useEffect(() => {
    if (!hasStackedInfo) return;
    const source = Array.isArray(orders) ? orders : [];

    console.log(
      "[InvoiceHistory] Hydrated stacked orders map (for unconsolidated invoices):"
    );
    stackedIds.forEach((rawId) => {
      const id = String(rawId || "").trim();
      if (!id) return;
      const order = source.find((o) => String(o?.id || "").trim() === id);
      if (!order) {
        console.log(
          "[InvoiceHistory] stackedOrders entry has no matching order in local state yet",
          { id }
        );
        return;
      }
      const invoice = order.invoice || {};
      const invoiceId =
        invoice.invoiceNumber ||
        invoice.invoice_number ||
        invoice.invoiceId ||
        order.invoiceId ||
        null;
      const pdfUrl = invoice.pdfUrl || order.invoicePdfUrl || null;

      console.log("[InvoiceHistory] stackedOrder invoice candidate", {
        id,
        hasInvoicePdf: Boolean(pdfUrl),
        invoiceId,
        pdfUrl,
      });
    });
  }, [hasStackedInfo, stackedIds, orders]);

  // Load outstanding balance from FreshBooks for this client
  useEffect(() => {
    if (!freshbooksClientIdValue) {
      setFbBalance(null);
      setFbBalanceError("");
      setFbBalanceLoading(false);
      return;
    }

    const base = process.env.REACT_APP_GET_OUTSTANDING_BALANCE_URL;
    if (!base) return;

    let cancelled = false;
    (async () => {
      try {
        setFbBalanceLoading(true);
        setFbBalanceError("");
        const url = `${base}?clientId=${encodeURIComponent(freshbooksClientIdValue)}`;
        const res = await fetch(url);
        const data = await res.json();
        if (cancelled) return;
        const raw = data?.balance;
        const n =
          typeof raw === "string"
            ? Number(raw.replace(/[^0-9.-]/g, ""))
            : Number(raw);
        setFbBalance(Number.isFinite(n) ? n : null);
        const rawOverdue = data?.overdue;
        const o =
          typeof rawOverdue === "string"
            ? Number(rawOverdue.replace(/[^0-9.-]/g, ""))
            : Number(rawOverdue);
        setFbOverdue(Number.isFinite(o) ? o : null);
      } catch (err) {
        if (!cancelled) {
          console.error("Failed to fetch FreshBooks outstanding balance:", err);
          setFbBalance(null);
          setFbOverdue(null);
          setFbBalanceError("Unable to load FreshBooks balance");
        }
      } finally {
        if (!cancelled) {
          setFbBalanceLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [freshbooksClientIdValue]);

  // Refresh payment status and invoice details via Cloud Function
  useEffect(() => {
    const ids = orders.map((o) => String(o?.id || "").trim()).filter(Boolean);
    if (!ids.length) return;

    // 120s cooldown between refresh calls
    const now = Date.now();
    if (now - lastRefreshRef.current < 120_000) return;
    lastRefreshRef.current = now;

    let cancelled = false;
    (async () => {
      try {
        setIsRefreshing(true);
        const result = await refreshOrderPaymentStatusClient(ids);
        const resultsArray = Array.isArray(result?.results)
          ? result.results
          : Array.isArray(result)
            ? result
            : [];
        if (!resultsArray.length || cancelled) return;

        const statusById = new Map();
        resultsArray.forEach((r) => {
          const id = String(r?.orderId || "").trim();
          if (!id || !r?.paymentStatus) return;
          statusById.set(id, r.paymentStatus);
        });
        if (!statusById.size) return;

        setOrders((prev) =>
          Array.isArray(prev)
            ? prev.map((order) => {
                const id = String(order?.id || "").trim();
                const nextStatus = id ? statusById.get(id) : undefined;
                if (!nextStatus) return order;
                return { ...order, paymentStatus: nextStatus };
              })
            : prev
        );
      } catch (err) {
        console.error("Failed to refresh invoice payment status:", err);
      } finally {
        if (!cancelled) {
          setIsRefreshing(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [orders.length]);

  const { standardInvoices, consolidatedInvoices, allInvoicedOrders } =
    useMemo(() => {
      const withInvoice = orders.filter((order) => {
        const invoice = order?.invoice;
        const hasPdf = invoice?.pdfUrl || order?.invoicePdfUrl;
        return Boolean(hasPdf);
      });

      const consolidated = [];
      const standard = [];

      withInvoice.forEach((order) => {
        const invoiceType = String(order?.invoice?.type || "").toLowerCase();
        const isInStack =
          hasStackedInfo &&
          stackedIds.some((id) => String(id) === String(order.id));

        const isMarkedConsolidated = invoiceType === "consolidated";

        if (hasStackedInfo) {
          // When we have stacked orders:
          // - If the order is still present in stackedOrders, it has NOT been
          //   consolidated yet -> keep in the non-consolidated list.
          // - If it is missing from stackedOrders, treat it as consolidated.
          if (isInStack) {
            standard.push(order);
          } else {
            consolidated.push(order);
          }
        } else {
          // Fallback when stackedOrders are not available:
          // rely on the invoice type flag if present.
          if (isMarkedConsolidated) {
            consolidated.push(order);
          } else {
            standard.push(order);
          }
        }
      });

      const sortByCreated = (list) =>
        list.slice().sort((a, b) => {
          const aDate = toDateSafe(a?.createdAt) || new Date(0);
          const bDate = toDateSafe(b?.createdAt) || new Date(0);
          return bDate - aDate;
        });

      const sortedStandard = sortByCreated(standard);

      // Group consolidated invoices by unique PDF URL so we don't duplicate rows
      // when multiple orders share the same consolidated invoice.
      const groupedByUrl = new Map();

      consolidated.forEach((order) => {
        const invoice = order?.invoice || {};
        const pdfUrl = invoice.pdfUrl || order?.invoicePdfUrl || "";
        const key = pdfUrl || `no-url:${order.id}`;

        if (!groupedByUrl.has(key)) {
          groupedByUrl.set(key, {
            pdfUrl,
            invoiceId:
              invoice.invoiceNumber ||
              invoice.invoice_number ||
              invoice.invoiceId ||
              order?.invoiceId ||
              String(order?.id || ""),
            total: 0,
            oldestCreatedAt: null,
            newestCreatedAt: null,
            orders: [],
          });
        }

        const group = groupedByUrl.get(key);
        group.orders.push(order);

        const invoiceTotal = getInvoiceTotal(order);
        if (Number.isFinite(invoiceTotal)) {
          group.total += invoiceTotal;
        }

        const currentDate = toDateSafe(order?.createdAt);
        if (currentDate) {
          if (!group.oldestCreatedAt || currentDate < group.oldestCreatedAt) {
            group.oldestCreatedAt = currentDate;
          }
          if (!group.newestCreatedAt || currentDate > group.newestCreatedAt) {
            group.newestCreatedAt = currentDate;
          }
        }
      });

      const consolidatedGroups = Array.from(groupedByUrl.values()).sort(
        (a, b) => {
          const aDate = toDateSafe(a.newestCreatedAt) || new Date(0);
          const bDate = toDateSafe(b.newestCreatedAt) || new Date(0);
          return bDate - aDate;
        }
      );

      return {
        standardInvoices: sortedStandard,
        consolidatedInvoices: consolidatedGroups,
        allInvoicedOrders: [...standard, ...consolidated],
      };
    }, [orders, stackedIds]);

  const totalInvoices = allInvoicedOrders.length;
  const totalBilled = allInvoicedOrders.reduce((sum, order) => {
    const n = getInvoiceTotal(order);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const overdueLocal = allInvoicedOrders.reduce((sum, order) => {
    if (!isOverdue(order)) return sum;
    const n = getInvoiceTotal(order);
    return sum + (Number.isFinite(n) ? n : 0);
  }, 0);
  const outstanding =
    fbBalance != null && Number.isFinite(fbBalance)
      ? Math.max(Number(fbBalance), 0)
      : null;
  const combinedForProgress =
    outstanding != null && totalBilled > 0 ? outstanding + totalBilled : null;
  const effectiveOverdue =
    fbOverdue != null && Number.isFinite(fbOverdue)
      ? Math.max(fbOverdue, 0)
      : overdueLocal;
  const outstandingPct =
    combinedForProgress && combinedForProgress > 0
      ? Math.min(100, Math.round((outstanding / combinedForProgress) * 100))
      : null;

  // For the segmented bar, we only care about how the outstanding amount
  // splits between overdue vs simply outstanding.
  const totalOutstandingForBar =
    outstanding != null && Number.isFinite(outstanding)
      ? Math.max(outstanding, 0)
      : 0;
  const overdueForBar =
    totalOutstandingForBar > 0 && effectiveOverdue != null
      ? Math.min(Math.max(effectiveOverdue, 0), totalOutstandingForBar)
      : 0;
  const currentDueForBar = Math.max(totalOutstandingForBar - overdueForBar, 0);
  const overduePctOfOutstanding =
    totalOutstandingForBar > 0
      ? Math.min(
          100,
          Math.max(0, (overdueForBar / totalOutstandingForBar) * 100)
        )
      : 0;
  const currentPctOfOutstanding =
    totalOutstandingForBar > 0
      ? Math.min(
          100,
          Math.max(0, (currentDueForBar / totalOutstandingForBar) * 100)
        )
      : 0;

  // Debug why the outstanding balance bar might not be visible / filled
  useEffect(() => {
    if (!freshbooksClientIdValue) {
      console.log(
        "[InvoiceHistory] Hiding FreshBooks summary: no freshbooksClientIdValue"
      );
      console.log(
        "[InvoiceHistory] Raw freshbooksClientId prop value",
        freshbooksClientId
      );
      return;
    }

    console.log("[InvoiceHistory] Balance debug", {
      fbBalance,
      fbOverdue,
      outstanding,
      totalBilled,
      combinedForProgress,
      outstandingPct,
      overdueLocal,
      effectiveOverdue,
      totalOutstandingForBar,
      overdueForBar,
      currentDueForBar,
      overduePctOfOutstanding,
      currentPctOfOutstanding,
    });

    if (outstandingPct == null) {
      console.log(
        "[InvoiceHistory] Progress bar width is 0 because outstandingPct is null (likely missing fbBalance or totalBilled <= 0)."
      );
    }
  }, [
    freshbooksClientIdValue,
    fbBalance,
    fbOverdue,
    outstanding,
    totalBilled,
    combinedForProgress,
    outstandingPct,
    overdueLocal,
    effectiveOverdue,
    totalOutstandingForBar,
    overdueForBar,
    currentDueForBar,
    overduePctOfOutstanding,
    currentPctOfOutstanding,
  ]);

  const activeInvoices =
    activeTab === "unconsolidated" ? standardInvoices : consolidatedInvoices;
  const [invoicePageSize, setInvoicePageSize] = useState(10);
  const [invoicePageIndex, setInvoicePageIndex] = useState(0);

  useEffect(() => {
    setInvoicePageIndex(0);
  }, [
    activeTab,
    invoicePageSize,
    standardInvoices.length,
    consolidatedInvoices.length,
  ]);

  const totalPages =
    activeInvoices.length > 0
      ? Math.max(1, Math.ceil(activeInvoices.length / invoicePageSize))
      : 1;
  const safePageIndex = Math.min(Math.max(0, invoicePageIndex), totalPages - 1);
  const pagedInvoices = useMemo(() => {
    if (!activeInvoices.length) return [];
    const start = safePageIndex * invoicePageSize;
    return activeInvoices.slice(start, start + invoicePageSize);
  }, [activeInvoices, safePageIndex, invoicePageSize]);

  return (
    <div className="cp-orders">
      <div className="cp-settings">
        <div className="cp-settings__header">
          <div className="cp-orders__toolbarTitle">
            <div className="cp-orders__sectionTitle">Invoice history</div>
            <div className="cp-orders__sectionSub">
              Download invoice PDFs and review totals for your orders.
            </div>
          </div>
        </div>
        {freshbooksClientIdValue ? (
          <div
            style={{
              marginBottom: 12,
              padding: 12,
              borderRadius: 12,
              border: "1px solid var(--ao-border)",
              background: "#f9fafb",
              color: "#0f172a",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 8,
                marginBottom: 6,
              }}
            >
              <div
                style={{
                  fontSize: 11,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  opacity: 0.7,
                }}
              >
                Freshbooks Billing overview
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-end",
                  gap: 2,
                  fontSize: 12,
                }}
              ></div>
            </div>

            <div
              style={{
                position: "relative",
                height: 8,
                borderRadius: 999,
                background: "rgba(148, 163, 184, 0.25)",
                overflow: "hidden",
              }}
            >
              {/* Base subtle gradient track */}
              <div
                style={{
                  position: "absolute",
                  inset: 0,
                  background:
                    "linear-gradient(90deg, rgba(148,201,64,0.08), rgba(34,197,94,0.12))",
                }}
              />

              {/* Only render fill when there is any outstanding balance */}
              {totalOutstandingForBar > 0 ? (
                <>
                  {/* Overdue portion (red) */}
                  {overduePctOfOutstanding > 0 ? (
                    <div
                      style={{
                        position: "absolute",
                        left: 0,
                        top: 0,
                        bottom: 0,
                        width: `${overduePctOfOutstanding}%`,
                        background: "#fecaca",
                        borderRadius:
                          overduePctOfOutstanding >= 100
                            ? 999
                            : "999px 0 0 999px",
                        transition: "width 0.4s ease",
                      }}
                    />
                  ) : null}

                  {/* Non-overdue outstanding portion (yellow) */}
                  {currentPctOfOutstanding > 0 ? (
                    <div
                      style={{
                        position: "absolute",
                        left: `${overduePctOfOutstanding}%`,
                        top: 0,
                        bottom: 0,
                        width: `${currentPctOfOutstanding}%`,
                        background: "#fde68a",
                        borderRadius:
                          overduePctOfOutstanding <= 0
                            ? 999
                            : "0 999px 999px 0",
                        transition: "width 0.4s ease",
                      }}
                    />
                  ) : null}
                </>
              ) : null}
            </div>

            <div
              style={{
                marginTop: 6,
                marginLeft: 5,
                marginRight: 5,
                display: "flex",
                justifyContent: "space-between",
                fontSize: 11,
                opacity: 0.75,
              }}
            >
              <span>
                Overdue:&nbsp;
                {formatMoney(overdueForBar)}
              </span>

              <span>
                Outstanding:&nbsp;
                {outstanding != null ? formatMoney(outstanding) : "Unknown"}
              </span>
            </div>
          </div>
        ) : null}

        <div className="cp-settings__section">
          <div className="cp-orders__panel">
            <div className="cp-orders__panelHeader">
              <div style={{ display: "flex", gap: 8 }}>
                <button
                  type="button"
                  className="op-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    border:
                      activeTab === "unconsolidated"
                        ? "1px solid #94c940"
                        : "1px solid var(--ao-border)",
                    background:
                      activeTab === "unconsolidated"
                        ? "#e5f4cf"
                        : "var(--ao-panel)",
                    color:
                      activeTab === "unconsolidated" ? "#052e16" : "#4b5563",
                  }}
                  onClick={() => setActiveTab("unconsolidated")}
                >
                  <span
                    aria-hidden="true"
                    style={{ fontSize: 14, lineHeight: 1 }}
                  >
                    ●
                  </span>
                  <span>Unconsolidated</span>
                </button>
                <button
                  type="button"
                  className="op-btn"
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 10px",
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 600,
                    border:
                      activeTab === "consolidated"
                        ? "1px solid #94c940"
                        : "1px solid var(--ao-border)",
                    background:
                      activeTab === "consolidated"
                        ? "#e5f4cf"
                        : "var(--ao-panel)",
                    color: activeTab === "consolidated" ? "#052e16" : "#4b5563",
                  }}
                  onClick={() => setActiveTab("consolidated")}
                >
                  <span
                    aria-hidden="true"
                    style={{ fontSize: 14, lineHeight: 1 }}
                  >
                    ⧉
                  </span>
                  <span>Consolidated</span>
                </button>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <div className="cp-orders__panelHint">
                  {activeTab === "unconsolidated"
                    ? "Invoices tied to individual orders that have not been consolidated."
                    : "Invoices that combine multiple orders into a single bill."}
                </div>
              </div>
            </div>

            <div className="cp-orders__list" role="list">
              {activeTab === "unconsolidated" ? (
                activeInvoices.length > 0 ? (
                  pagedInvoices.map((order) => {
                    const id = String(order?.id || "");
                    const shortOrderId = id ? id.slice(0, 8) : "—";
                    const invoice = order?.invoice || {};
                    const invoiceId =
                      invoice.invoiceNumber ||
                      invoice.invoice_number ||
                      invoice.invoiceId ||
                      order?.invoiceId ||
                      shortOrderId;
                    const pdfUrl =
                      order?.invoice?.pdfUrl || order?.invoicePdfUrl || "";
                    const canOpen = Boolean(pdfUrl);
                    const overdue = isOverdue(order);
                    const topRightStatusLabel = "Pending";

                    return (
                      <button
                        key={order.id || invoiceId}
                        type="button"
                        className="cp-orders__row"
                        style={
                          overdue
                            ? {
                                boxShadow:
                                  "0 0 0 1px #fecaca, 0 0 0 4px rgba(248,113,113,0.35)",
                                borderColor: "#fecaca",
                              }
                            : undefined
                        }
                        onClick={() => {
                          if (!canOpen) return;
                          window.open(pdfUrl, "_blank", "noopener,noreferrer");
                        }}
                        disabled={!canOpen}
                      >
                        <div className="cp-orders__rowTL">
                          <div className="cp-orders__orderId">
                            Invoice for order{" "}
                            {invoiceId ? `#${invoiceId}` : "—"}
                          </div>
                        </div>

                        <div className="cp-orders__rowTR">
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "2px 8px",
                              borderRadius: 999,
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                              ...getStatusPillStyle(topRightStatusLabel),
                            }}
                          >
                            {topRightStatusLabel}
                          </span>
                        </div>

                        <div className="cp-orders__rowBL">
                          <div className="cp-orders__rowBottom">
                            <span>{formatDateTime(order?.createdAt)}</span>
                          </div>
                        </div>
                        <div className="cp-orders__rowBR">
                          <div className="cp-orders__total">
                            {formatMoney(order?.subtotal)}
                          </div>
                          <div className="cp-orders__chev" aria-hidden="true">
                            ›
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div style={{ padding: 12 }}>
                    <div className="cp-orders__empty">
                      You don’t have any single-order (non-consolidated)
                      invoices yet.
                    </div>
                  </div>
                )
              ) : activeInvoices.length > 0 ? (
                pagedInvoices.map((group) => {
                  const primaryOrder = group.orders[0] || {};
                  const primaryInvoice = primaryOrder?.invoice || {};
                  const invoiceId =
                    group.invoiceId ||
                    primaryInvoice.invoiceNumber ||
                    primaryInvoice.invoice_number ||
                    primaryInvoice.invoiceId ||
                    primaryOrder?.invoiceId ||
                    String(primaryOrder?.id || "");
                  const pdfUrl = group.pdfUrl;
                  const canOpen = Boolean(pdfUrl);
                  const startLabel = formatShortDate(group.oldestCreatedAt);
                  const endLabel = formatShortDate(group.newestCreatedAt);
                  const paymentStatusLabel = summarizePaymentStatuses(
                    group.orders
                  );
                  const overdueGroup = group.orders.some(isOverdue);
                  const topRightStatusLabel = summarizeInvoiceStatusLabel(
                    group.orders
                  );

                  return (
                    <button
                      key={pdfUrl || invoiceId}
                      type="button"
                      className="cp-orders__row"
                      style={
                        overdueGroup
                          ? {
                              boxShadow:
                                "0 0 0 1px #fecaca, 0 0 0 4px rgba(248,113,113,0.35)",
                              borderColor: "#fecaca",
                            }
                          : undefined
                      }
                      onClick={() => {
                        if (!canOpen) return;
                        window.open(pdfUrl, "_blank", "noopener,noreferrer");
                      }}
                      disabled={!canOpen}
                    >
                      <div className="cp-orders__rowTL">
                        <div className="cp-orders__orderId">
                          Invoice for {startLabel}-{endLabel}
                        </div>
                      </div>

                      <div className="cp-orders__rowTR">
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            padding: "2px 8px",
                            borderRadius: 999,
                            fontSize: 10,
                            fontWeight: 700,
                            letterSpacing: "0.08em",
                            textTransform: "uppercase",
                            ...getStatusPillStyle(topRightStatusLabel),
                          }}
                        >
                          {topRightStatusLabel}
                        </span>
                        {overdueGroup && topRightStatusLabel !== "Overdue" ? (
                          <span
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              padding: "2px 8px",
                              borderRadius: 999,
                              border: "1px solid #fecaca",
                              background: "#fef2f2",
                              color: "#b91c1c",
                              fontSize: 10,
                              fontWeight: 700,
                              letterSpacing: "0.08em",
                              textTransform: "uppercase",
                            }}
                          >
                            OVERDUE
                          </span>
                        ) : null}
                      </div>

                      <div className="cp-orders__rowBL">
                        <div className="cp-orders__rowBottom">
                          <span>
                            Invoice {invoiceId ? `#${invoiceId}` : "—"}
                          </span>
                          <span>•</span>
                          <span>
                            Includes {group.orders.length}{" "}
                            {group.orders.length === 1 ? "order" : "orders"}
                          </span>
                          <span>•</span>
                          <span>Payment: {paymentStatusLabel}</span>
                        </div>
                      </div>

                      <div className="cp-orders__rowBR">
                        <div className="cp-orders__total">
                          {formatMoney(group?.total)}
                        </div>
                        <div className="cp-orders__chev" aria-hidden="true">
                          ›
                        </div>
                      </div>
                    </button>
                  );
                })
              ) : (
                <div style={{ padding: 12 }}>
                  <div className="cp-orders__empty">
                    You don’t have any consolidated invoices yet.
                  </div>
                </div>
              )}
            </div>
            {activeInvoices.length > invoicePageSize ? (
              <div
                className="cp-orders__pagination"
                role="navigation"
                aria-label="Invoices pagination"
              >
                <div className="cp-orders__paginationLeft">
                  <span className="cp-orders__paginationText">
                    Page {safePageIndex + 1} of {totalPages}
                  </span>
                  <span className="cp-orders__paginationText">
                    Showing{" "}
                    {Math.min(
                      activeInvoices.length,
                      safePageIndex * invoicePageSize + 1
                    )}
                    –
                    {Math.min(
                      activeInvoices.length,
                      (safePageIndex + 1) * invoicePageSize
                    )}{" "}
                    of {activeInvoices.length}
                  </span>
                </div>

                <div className="cp-orders__paginationRight">
                  <label className="cp-orders__paginationText">
                    Per page{" "}
                    <select
                      className="cp-orders__select cp-orders__select--sm"
                      value={invoicePageSize}
                      onChange={(e) =>
                        setInvoicePageSize(Number(e.target.value))
                      }
                    >
                      <option value={10}>10</option>
                      <option value={20}>20</option>
                      <option value={50}>50</option>
                    </select>
                  </label>

                  <span
                    role="button"
                    tabIndex={0}
                    className="op-btn op-btn--ghost"
                    onClick={() =>
                      setInvoicePageIndex((p) => Math.max(0, p - 1))
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setInvoicePageIndex((p) => Math.max(0, p - 1));
                      }
                    }}
                    aria-disabled={safePageIndex <= 0}
                  >
                    Prev
                  </span>
                  <span
                    role="button"
                    tabIndex={0}
                    className="op-btn op-btn--ghost"
                    onClick={() =>
                      setInvoicePageIndex((p) =>
                        Math.min(totalPages - 1, p + 1)
                      )
                    }
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        setInvoicePageIndex((p) =>
                          Math.min(totalPages - 1, p + 1)
                        );
                      }
                    }}
                    aria-disabled={safePageIndex >= totalPages - 1}
                  >
                    Next
                  </span>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}
